// Thin fetch wrapper around the generated OpenAPI types (schema.d.ts).
// The access token lives in memory only; the refresh token is an httpOnly
// cookie scoped to /api/v1/auth (spec §4.3), rotated on every refresh.
import type { components } from "./schema";

export type S = components["schemas"];
export type LegalMove = S["LegalMove"];
export type Position = S["PositionOut"];
export type MoveResult = S["MoveOut"];
export type AttemptResult = S["AttemptResult"];
export type Me = S["MeOut"];
export type Solution = S["SolutionOut"];

const BASE = "/api/v1";
let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
const listeners = new Set<() => void>();

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
  ) {
    super(detail);
  }
}

export function onAuthChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function setToken(token: string | null) {
  accessToken = token;
  listeners.forEach((fn) => fn());
}

export const isLoggedIn = () => accessToken !== null;

export function guestId(): string | null {
  try {
    return localStorage.getItem("dt.guest");
  } catch {
    return null;
  }
}

export function setGuestId(id: string) {
  try {
    localStorage.setItem("dt.guest", id);
  } catch {
    /* private mode: the id lives for this page only */
  }
}

async function refresh(): Promise<boolean> {
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const r = await fetch(`${BASE}/auth/refresh`, {
          method: "POST",
          credentials: "include",
          headers: { "X-Requested-With": "XMLHttpRequest" },
        });
        if (!r.ok) {
          setToken(null);
          return false;
        }
        setToken((await r.json()).access_token);
        return true;
      } catch {
        return false;
      } finally {
        setTimeout(() => (refreshing = null), 0);
      }
    })();
  }
  return refreshing;
}

/** At startup: a refresh cookie, if any, gives us an access token. */
export const restoreSession = () => refresh();

type Options = { method?: string; body?: unknown; form?: FormData; retry?: boolean };

export async function api<T>(path: string, opts: Options = {}): Promise<T> {
  const headers: Record<string, string> = { "X-Requested-With": "XMLHttpRequest" };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  const guest = guestId();
  if (guest && !accessToken) headers["X-Guest-Id"] = guest;
  let body: BodyInit | undefined;
  if (opts.form) body = opts.form;
  else if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  const r = await fetch(BASE + path, {
    method: opts.method ?? (body ? "POST" : "GET"),
    headers,
    body,
    credentials: "include",
  });
  if (r.status === 401 && accessToken && opts.retry !== false) {
    if (await refresh()) return api<T>(path, { ...opts, retry: false });
  }
  if (!r.ok) {
    let detail = r.statusText;
    try {
      const j = await r.json();
      detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
    } catch {
      /* not json */
    }
    throw new ApiError(r.status, detail);
  }
  const type = r.headers.get("content-type") ?? "";
  if (type.includes("application/json")) {
    const data = await r.json();
    if (data && typeof data === "object" && "guest_id" in data && data.guest_id) setGuestId(data.guest_id);
    return data as T;
  }
  return (await r.text()) as unknown as T;
}

export async function login(email: string, password: string) {
  const r = await api<S["TokenOut"]>("/auth/login", { body: { email, password } });
  setToken(r.access_token);
}

export async function register(email: string, password: string, display_name: string) {
  const r = await api<S["TokenOut"]>("/auth/register", { body: { email, password, display_name } });
  setToken(r.access_token);
}

export async function logout() {
  try {
    await api("/auth/logout", { method: "POST" });
  } finally {
    setToken(null);
  }
}

/** Download a file from an authenticated endpoint. */
export async function download(path: string, filename: string) {
  const r = await fetch(BASE + path, {
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    credentials: "include",
  });
  if (!r.ok) throw new ApiError(r.status, r.statusText);
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
